import { nanoid } from 'nanoid';
import type { HotspotEvents } from '@types';
import { $loadedScene } from '@stores/loadedStore';
import { MainPlayer } from '@stores/krpPlayerStores';
import { $sceneMapApi } from '@stores/dataStores';
import { sendWarning } from '@components/Noti';
import { $GateRotateItemEditing } from '@stores/autoRotateStores';
import { krpHideTip, krpShowTip } from '@utils/krpShotTip';
import { ROTATE_START_ID, ROTATE_START_ICON, ROTATE_MAX } from '../constants';
import { viewDefault } from '../../view';
import { onRotateItemClick } from './onRotateItemClick';

const getLineId = (id: string) => `${id}line`;

const onHotspotClick: HotspotEvents['onHotspotClick'] = ({ id }) => {
  onRotateItemClick(id);
};

const onHotspotMoving: HotspotEvents['onHotspotMoving'] = ({
  id,
  ath,
  atv,
}) => {
  const scene = $loadedScene.getState();
  if (!scene) return;
  const list = scene.config.autoRotate?.tourData ?? [];
  const index = list.findIndex((item) => item.id === id);
  if (index < 0) return;
  const curSpot = MainPlayer.get()?.getHotspotById(getLineId(id));
  if (curSpot) {
    MainPlayer.get()?.updatePolygonHotspot({
      id: getLineId(id),
      points: [curSpot.points[0], { ath, atv }],
    });
  }
  const next = list[index + 1];
  if (!next) return;
  const nextSpot = MainPlayer.get()?.getHotspotById(getLineId(next.id));
  if (nextSpot) {
    MainPlayer.get()?.updatePolygonHotspot({
      id: getLineId(next.id),
      points: [{ ath, atv }, nextSpot.points[1]],
    });
  }
};

const onHotspotMoveEnd: HotspotEvents['onHotspotMoveEnd'] = ({ id }) => {
  const scene = $loadedScene.getState();
  if (!scene) return;
  const list = scene.config.autoRotate?.tourData ?? [];
  const index = list.findIndex((item) => item.id === id);
  if (index < 0) return;
  const { ath = 0, atv = 0 } = MainPlayer.get()?.getHotspotById(id) ?? {};
  $sceneMapApi.updateScene({
    id: scene.id,
    updater: (draft) => {
      const item = draft.config.autoRotate!.tourData![index];
      item.hlookat = ath;
      item.vlookat = atv;
    },
  });
};

const addItem = ({
  id,
  atv,
  ath,
  text,
  prevAth,
  prevAtv,
}: {
  id: string;
  text: string;
  ath: number;
  atv: number;
  prevAth: number;
  prevAtv: number;
}) => {
  MainPlayer.get()?.addTextHotspot({
    width: 34,
    height: 34,
    autoResize: false,
    id,
    text,
    ath,
    atv,
    distorted: false,
    dragable: true,
    align: 'center',
    vCenter: true,
    font: {
      fontSize: 14,
      fontColor: '#000000',
    },
    background: {
      bgEnabled: true,
      bgColor: '#ffffff',
      bgAlpha: 1,
      bgRoundedge: 17,
      borderWidth: 0,
    },
    events: {
      onHotspotMoving: (e) => {
        onHotspotMoving(e);
        krpHideTip({ id: e.id });
      },
      onHotspotClick,
      onHotspotMoveEnd,
      onHotspotOver: (e) => {
        krpShowTip({ id: e.id, text: '编辑节点' });
      },
      onHotspotOut: (e) => {
        krpHideTip({ id: e.id });
      },
    },
  });
  MainPlayer.get()?.addPolygonHotspot({
    id: getLineId(id),
    points: [
      { ath: prevAth, atv: prevAtv },
      { ath, atv },
    ],
    borderWidth: 3,
    borderColor: '#FA6400',
    borderAlpha: 1,
    overBorderWidth: 3,
    overBorderColor: '#FA6400',
    overBorderAlpha: 1,
  });
};

export function krpAddCustomRotate() {
  const scene = $loadedScene.getState();
  if (!scene) return;
  const list = scene.config.autoRotate?.tourData ?? [];
  const { hLookAt = 0, vLookAt = 0 } = $loadedScene.getState()?.view ?? {};
  MainPlayer.get()?.addImageHotspot({
    id: ROTATE_START_ID,
    urls: [ROTATE_START_ICON],
    ath: hLookAt,
    atv: vLookAt,
    scaleX: 0.5,
    scaleY: 0.5,
    zOrder: 0,
    dragable: false,
    distorted: false,
    events: {
      onHotspotOver: (e) => {
        krpShowTip({ id: e.id, text: '起始节点由初始视角决定，不可拖动' });
      },
      onHotspotOut: (e) => {
        krpHideTip({ id: e.id });
      },
    },
  });
  list.forEach((x, i) => {
    const lastItem = list[i - 1];
    addItem({
      id: x.id,
      text: (i + 1).toString(),
      ath: x.hlookat,
      atv: x.vlookat,
      prevAth: lastItem ? lastItem.hlookat : scene.view.hLookAt,
      prevAtv: lastItem ? lastItem.vlookat : scene.view.vLookAt,
    });
  });
  const editingId = $GateRotateItemEditing.state.getState()?.id;
  if (editingId) {
    MainPlayer.get()?.updateTextHotspot({
      id: editingId,
      font: { fontColor: '#ffffff' },
      background: { bgColor: '#FA6400' },
    });
  }
}

export function krpRemoveCustomRotate(opts: { withoutStart?: boolean } = {}) {
  if (!opts.withoutStart) {
    MainPlayer.get()?.removeHotspotById(ROTATE_START_ID);
  }
  const scene = $loadedScene.getState();
  if (!scene) return;
  const list = scene.config.autoRotate?.tourData ?? [];
  list.forEach((x) => {
    MainPlayer.get()?.removeHotspotById(x.id);
    MainPlayer.get()?.removeHotspotById(getLineId(x.id));
  });
}

export function addRotateItem({ ath, atv }: { ath: number; atv: number }) {
  const scene = $loadedScene.getState();
  if (!scene) return;
  const list = scene.config.autoRotate?.tourData ?? [];
  if (list.length + 1 >= ROTATE_MAX) {
    sendWarning(`添加失败，总节点数不超过${ROTATE_MAX}个`);
    return;
  }
  const id = `r_${nanoid()}`;
  const index = list.length + 1;
  const lastItem = list[list.length - 1];
  addItem({
    id,
    text: index.toString(),
    ath,
    atv,
    prevAth: lastItem ? lastItem.hlookat : scene.view.hLookAt,
    prevAtv: lastItem ? lastItem.vlookat : scene.view.vLookAt,
  });
  const startAt = lastItem ? lastItem.startAt + lastItem.duration : 0;
  $sceneMapApi.updateScene({
    id: scene.id,
    updater: (draft) => {
      if (!draft.config.autoRotate) return;
      draft.config.autoRotate.tourData ??= [];
      const { tourData } = draft.config.autoRotate;
      tourData.push({
        id,
        startAt,
        duration: 5,
        hlookat: ath,
        vlookat: atv,
        fov: MainPlayer.get()?.getView().fov ?? viewDefault.fov,
        action: 0,
      });
    },
  });
}

export function insertRotateItem({ index }: { index: number }) {
  const scene = $loadedScene.getState();
  if (!scene) return;
  const list = scene.config.autoRotate?.tourData ?? [];
  if (list.length + 1 >= ROTATE_MAX) {
    sendWarning(`插入失败，总节点数不超过${ROTATE_MAX}个`);
    return;
  }
  const item = list[index];
  if (!item) return;
  const { startAt, duration, hlookat, vlookat, fov } = item;
  if (duration < 2) {
    sendWarning('该时间段少于2秒不能插入');
    return;
  }
  krpRemoveCustomRotate();
  const prevAtv = list[index - 1]?.vlookat ?? scene.view.vLookAt;
  const prevAth = list[index - 1]?.hlookat ?? scene.view.hLookAt;
  const prevFov = list[index - 1]?.fov ?? scene.view.fov;
  const id = `r_${nanoid()}`;
  const newDur = duration / 2;
  $sceneMapApi.updateScene({
    id: scene.id,
    updater: (draft) => {
      if (!draft.config.autoRotate) return;
      draft.config.autoRotate.tourData ??= [];
      const { tourData } = draft.config.autoRotate;
      tourData[index].duration = newDur;
      tourData[index].startAt = startAt + newDur;
      tourData?.splice(index, 0, {
        id,
        startAt,
        duration: newDur,
        hlookat: (hlookat + prevAth) / 2,
        vlookat: (vlookat + prevAtv) / 2,
        fov: (fov + prevFov) / 2,
        action: 0,
      });
    },
  });
  krpAddCustomRotate();
}

export function deleteRotateItem({ id }: { id: string }) {
  const scene = $loadedScene.getState();
  if (!scene) return;
  krpRemoveCustomRotate();
  $sceneMapApi.updateScene({
    id: scene.id,
    updater: (draft) => {
      if (!draft.config.autoRotate) return;
      draft.config.autoRotate.tourData ??= [];
      const { tourData } = draft.config.autoRotate;
      const index = tourData.findIndex((x) => x.id === id);
      if (index < 0) return;
      const item = tourData[index];
      const nextItem = tourData[index + 1];
      if (nextItem) {
        nextItem.startAt = item.startAt;
        nextItem.duration = item.duration + nextItem.duration;
      }
      tourData.splice(index, 1);
    },
  });
  krpAddCustomRotate();
}
